import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notification } from 'antd';
import lib from '../../../assets/utils/lib';
import ClaimsBatchList from './ClaimsBatchList';
import BatchDetailView from './BatchDetailView';
import BatchFilters from './BatchFilters';
import GroupCard from './GroupCard';
import BatchLoadingSkeleton from './BatchLoadingSkeleton';
import { Row, Col, Pagination, Empty, Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './ClaimsBatchingPage.css';

/**
 * ClaimsBatchingPage - Main container for facility claims batching
 * Manages hierarchical navigation: Month → Week → Encounter Date → Claims
 */
const ClaimsBatchingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize from URL params for deep linking
  const urlView = searchParams.get('view') || 'batches';
  const urlLevel = searchParams.get('level');
  const urlBatchDate = searchParams.get('batch');
  const urlPage = parseInt(searchParams.get('page') || '1');
  const urlDateStart = searchParams.get('start_date');
  const urlDateEnd = searchParams.get('end_date');
  const urlSearch = searchParams.get('search') || '';
  const urlStatus = searchParams.get('status');
  const urlGroupBy = searchParams.get('group_by') || 'month';

  // Determine initial level based on groupBy if not in URL
  const getInitialLevel = () => {
    if (urlLevel) return urlLevel;
    if (urlGroupBy === 'encounter_date') return 'encounter_dates';
    if (urlGroupBy === 'week') return 'weeks';
    if (urlGroupBy === 'year') return 'months'; // Years show as months for now
    return 'months'; // Default
  };

  const [view, setView] = useState(urlView); // 'batches' | 'detail'
  const [level, setLevel] = useState(getInitialLevel()); // 'months' | 'weeks' | 'encounter_dates' | 'claims'
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [groups, setGroups] = useState([]); // Can be months, weeks, or encounter dates
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: urlPage,
    pageSize: 20,
    total: 0,
  });

  // Filter states - initialize from URL
  const [dateRange, setDateRange] = useState(
    urlDateStart && urlDateEnd 
      ? [dayjs(urlDateStart), dayjs(urlDateEnd)] 
      : null
  );
  const [searchValue, setSearchValue] = useState(urlSearch);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [groupBy, setGroupBy] = useState(urlGroupBy); // 'month' | 'week' | 'year' | 'encounter_date'

  // Debounce search (ref to avoid stale closure in effect cleanup)
  const searchTimeoutRef = useRef(null);

  // Update URL when state changes (deep linking)
  const updateURL = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load batch from URL if deep linked
  useEffect(() => {
    if (urlBatchDate && urlView === 'detail') {
      // Try to find batch in current groups (encounter dates)
      const existingBatch = groups.find(b => (b.encounter_date || b.key) === urlBatchDate);
      if (existingBatch) {
        setSelectedBatch(existingBatch);
        setView('detail');
        setLevel('claims');
      } else if (groups.length > 0) {
        // Batch not found in current list, create a minimal batch object
        setSelectedBatch({ encounter_date: urlBatchDate, key: urlBatchDate });
        setView('detail');
        setLevel('claims');
      }
    }
  }, [urlBatchDate, urlView, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine current group_by based on level and selected items
  const getCurrentGroupBy = useCallback(() => {
    if (level === 'months') return 'month';
    if (level === 'weeks') return 'week';
    if (level === 'encounter_dates') return 'encounter_date';
    return groupBy; // Use filter groupBy for top level
  }, [level, groupBy]);

  const fetchGroups = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const currentGroupBy = getCurrentGroupBy();
      const params = {
        page,
        pageSize: pagination.pageSize,
        group_by: currentGroupBy,
      };

      // Add filters based on selected hierarchy
      if (selectedMonth && level === 'weeks') {
        params.month = selectedMonth.key || selectedMonth.month || selectedMonth;
      }
      if (selectedWeek && level === 'encounter_dates') {
        params.week = selectedWeek.key || selectedWeek.week || selectedWeek;
      }
      if (selectedMonth && level === 'encounter_dates') {
        params.month = selectedMonth.key || selectedMonth.month || selectedMonth;
      }

      // Add date range filter
      if (dateRange && dateRange.length === 2) {
        params.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
        params.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
      }

      // Add search filter
      if (searchValue) {
        params.search = searchValue;
      }

      // Add status filter
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Ensure format=grouped (default) for hierarchical grouping
      params.format = 'grouped';

      const response = await lib.getFacilityClaimsBatches(params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch groups',
        });
        return;
      }

      // Backend returns: { error, message, format, data: [...groups...], grouping: {...}, pagination: {...} }
      // data is an array directly, not nested
      const responseData = response?.data || {};
      const groupsList = Array.isArray(responseData.data) ? responseData.data : [];
      
      setGroups(groupsList);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || groupsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching groups:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch groups',
      });
    } finally {
      setLoading(false);
    }
  }, [
    getCurrentGroupBy,
    pagination.pageSize,
    selectedMonth,
    selectedWeek,
    level,
    dateRange,
    searchValue,
    statusFilter,
  ]);

  // Fetch groups when page, filters, or level changes
  useEffect(() => {
    if (view === 'batches' && level !== 'claims') {
      fetchGroups(pagination.current);
    }
  }, [pagination, view, level, dateRange, statusFilter, groupBy, selectedMonth, selectedWeek, fetchGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (view === 'batches' && level !== 'claims') {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchGroups(1); // Reset to page 1 on search
      }, 500);
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [searchValue, view, level, fetchGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGroupClick = (group) => {
    if (level === 'months') {
      // Drill down to weeks
      setSelectedMonth(group);
      setLevel('weeks');
      updateURL({ level: 'weeks', page: '1' });
    } else if (level === 'weeks') {
      // Drill down to encounter dates
      setSelectedWeek(group);
      setLevel('encounter_dates');
      updateURL({ level: 'encounter_dates', page: '1' });
    } else if (level === 'encounter_dates') {
      // Show claims detail
      setSelectedBatch(group);
      setLevel('claims');
      setView('detail');
      updateURL({ level: 'claims', view: 'detail', batch: group.encounter_date || group.key });
    }
  };

  const handleBack = () => {
    if (level === 'claims') {
      // Go back to encounter dates
      setLevel('encounter_dates');
      setView('batches');
      setSelectedBatch(null);
      updateURL({ level: 'encounter_dates', view: 'batches', batch: null });
    } else if (level === 'encounter_dates') {
      // Go back to weeks
      setLevel('weeks');
      setSelectedWeek(null);
      updateURL({ level: 'weeks', page: '1' });
    } else if (level === 'weeks') {
      // Go back to months
      setLevel('months');
      setSelectedMonth(null);
      setSelectedWeek(null);
      updateURL({ level: 'months', page: '1' });
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    updateURL({ page: page.toString() });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to page 1
    if (dates && dates.length === 2) {
      updateURL({
        start_date: dayjs(dates[0]).format('YYYY-MM-DD'),
        end_date: dayjs(dates[1]).format('YYYY-MM-DD'),
        page: '1',
      });
    } else {
      updateURL({ start_date: null, end_date: null, page: '1' });
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to page 1
    updateURL({ search: value || null, page: '1' });
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to page 1
    updateURL({ status: value || null, page: '1' });
  };

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    // Reset hierarchy when changing group by
    setLevel('months');
    setSelectedMonth(null);
    setSelectedWeek(null);
    setSelectedBatch(null);
    setPagination((prev) => ({ ...prev, current: 1 }));
    updateURL({ group_by: value, level: 'months', page: '1' });
  };

  const handleClearFilters = () => {
    setDateRange(null);
    setSearchValue('');
    setStatusFilter(null);
    setGroupBy('month');
    setLevel('months');
    setSelectedMonth(null);
    setSelectedWeek(null);
    setSelectedBatch(null);
    setPagination((prev) => ({ ...prev, current: 1 }));
    updateURL({
      start_date: null,
      end_date: null,
      search: null,
      status: null,
      group_by: 'month',
      level: 'months',
      page: '1',
    });
  };

  // Get breadcrumbs based on current level
  const getBreadcrumbs = () => {
    const crumbs = [
      { title: <><HomeOutlined /> Claims</>, onClick: () => { setLevel('months'); setSelectedMonth(null); setSelectedWeek(null); updateURL({ level: 'months', page: '1' }); } },
    ];
    if (selectedMonth) {
      const monthLabel = selectedMonth.label || dayjs(selectedMonth.key || selectedMonth.month + '-01').format('MMMM YYYY');
      crumbs.push({
        title: monthLabel,
        onClick: () => { setLevel('weeks'); setSelectedWeek(null); updateURL({ level: 'weeks', page: '1' }); }
      });
    }
    if (selectedWeek) {
      const weekLabel = selectedWeek.label || selectedWeek.key || 'Week';
      crumbs.push({
        title: weekLabel,
        onClick: () => { setLevel('encounter_dates'); updateURL({ level: 'encounter_dates', page: '1' }); }
      });
    }
    if (level === 'claims') {
      crumbs.push({ title: 'Claims Detail' });
    }
    return crumbs;
  };

  const getGroupType = () => {
    if (level === 'months') return 'month';
    if (level === 'weeks') return 'week';
    if (level === 'encounter_dates') return 'encounter_date';
    return 'month';
  };

  return (
    <div className="claims-batching-page">
      {view === 'batches' ? (
        <>
          <BatchFilters
            dateRange={dateRange}
            searchValue={searchValue}
            statusFilter={statusFilter}
            groupBy={groupBy}
            onDateRangeChange={handleDateRangeChange}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
            onGroupByChange={handleGroupByChange}
            onClearFilters={handleClearFilters}
          />
          
          {(level !== 'months' || selectedMonth || selectedWeek) && (
            <Breadcrumb
              items={getBreadcrumbs()}
              style={{ marginBottom: '16px' }}
            />
          )}

          {loading ? (
            <BatchLoadingSkeleton />
          ) : groups.length === 0 ? (
            <Empty description="No groups found" />
          ) : (
            <>
              <div className="claims-batching-page__grid">
                <Row gutter={[16, 16]}>
                  {groups.map((group, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={group.key || group.encounter_date || index}>
                      <GroupCard
                        group={group}
                        groupType={getGroupType()}
                        onClick={() => handleGroupClick(group)}
                      />
                    </Col>
                  ))}
                </Row>
              </div>

              {pagination.total > pagination.pageSize && (
                <div className="claims-batching-page__pagination">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} ${level}`
                    }
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <BatchDetailView
          batch={selectedBatch}
          encounterDate={selectedBatch?.encounter_date || selectedBatch?.key}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default ClaimsBatchingPage;

